import { useApolloClient } from '@apollo/client';
import { RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mdiDownload } from '@mdi/js';

import {
    IOfferEventDetailFragment,
    IOfferEventSubscriptionStatus,
    useCreateOfferEventEnrollmentsMutation,
    useDeleteOfferEventEnrollmentsMutation,
    useUpdateOfferEventEnrollmentsMutation,
    useCreateCertificateForEnrollmentsMutation,
    IContentTypeValue,
    useExportOfferEventEnrollmentMutation,
    useCurrentUserQuery,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { useOfferEventManageableEnrollmentList } from 'offer/hooks/useOfferEventManageableEnrollmentList';
import { SubscriptionTable } from 'offer/tables/SubscriptionsTable';
import { TableAddButton } from 'common/components/Table';
import { Typography } from 'common/components/Typography';
import { UserSelectDrawer } from 'user/components/UserSelector/UserSelectDrawer';
import { ConfirmDialog } from 'common/components/ConfirmDialog';
import { getExtraFieldsJSON } from 'common/utils/extraFields';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { Button } from 'common/components/Button';
import { Icon } from 'common/components/Icon';

interface IProps {
    drawerRef?: RefObject<HTMLDivElement>;
    offerEvent: IOfferEventDetailFragment;
}

export const OfferEventMangeableEnrollmentList = ({
    drawerRef,
    offerEvent,
}: IProps) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const [displaySnackbar] = useSnackbar();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [exportReport, { loading: loadingExport }] =
        useExportOfferEventEnrollmentMutation();
    const { data: currentUserData } = useCurrentUserQuery();
    const currentUserEmail = currentUserData?.currentUser?.email;

    const client = useApolloClient();
    const prevWindowTop = useRef(drawerRef?.current?.scrollTop || 0);

    const {
        accessTrainings,
        accessExternalContents,
        accessCourses,
        accessScorms,
    } = offerEvent;

    // Only include progress if there is any material having the ability to have progress
    const includeProgress =
        !!accessTrainings.edges.length ||
        !!accessExternalContents.edges.length ||
        !!accessCourses.edges.length ||
        !!accessScorms.edges.length;

    const {
        enrollments,
        loading: subscriptionsLoading,
        handleSearch,
        handleFilterSelect,
        searchQueryParam,
        selectionProps,
        filters,
        filtersLoading,
        selectedFilters,
        paginationSettings,
        setPage,
    } = useOfferEventManageableEnrollmentList(
        offerEvent.id,
        undefined, // Disabled users are not used in this component
        includeProgress
    );

    const [updateEnrollment] = useUpdateOfferEventEnrollmentsMutation();
    const [deleteEnrollments, { loading: deleteEnrollmentsLoading }] =
        useDeleteOfferEventEnrollmentsMutation();
    const [createEnrollments, { loading: createEnrollmentsLoading }] =
        useCreateOfferEventEnrollmentsMutation();
    const [createCertificates, { loading: createCertificatesLoading }] =
        useCreateCertificateForEnrollmentsMutation();

    const loading =
        subscriptionsLoading ||
        createEnrollmentsLoading ||
        deleteEnrollmentsLoading ||
        createCertificatesLoading;

    useEffect(() => {
        if (subscriptionsLoading) return;

        // Makes sure the drawer is scrolled to the position it was before
        // after doing a mutation. The mutations will cause the table to refresh
        // and scroll the drawer to the top.
        if (drawerRef?.current && prevWindowTop.current) {
            drawerRef.current.scrollTo(0, prevWindowTop.current);

            prevWindowTop.current = 0;
        }
    }, [subscriptionsLoading]);

    const evictEnrollmentsFromCache = (skipForEvent?: boolean) => {
        client.cache.evict({
            fieldName: 'manageableOfferEventEnrollments',
        });

        if (!skipForEvent) {
            client.cache.evict({
                fieldName: 'manageableOfferEventEnrollmentsForEvent',
            });
        }

        client.cache.evict({
            fieldName: 'paginatedOfferEvents',
        });

        client.cache.gc();
    };

    const handleCreateEnrollments = async (users: string[]) => {
        // Save scroll position before doing the mutation
        prevWindowTop.current = drawerRef?.current?.scrollTop || 0;

        try {
            await createEnrollments({
                variables: {
                    offerEventId: offerEvent.id,
                    users,
                    includeProgress,
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        evictEnrollmentsFromCache();

        // Refetch the selector to make sure the correct users are selected
        client.cache.evict({ fieldName: 'paginatedUsers' });
        client.cache.gc();

        setDrawerOpen(false);

        return;
    };

    const handleUpdateSubscription = async (
        enrollmentIds: string[],
        status: IOfferEventSubscriptionStatus,
        reason: string,
        generateCertificate: boolean
    ) => {
        try {
            await updateEnrollment({
                variables: {
                    offerEventEnrollmentIds: enrollmentIds,
                    status,
                    reason,
                    includeProgress,
                },
            });

            if (generateCertificate) {
                await createCertificates({
                    variables: {
                        offerEventEnrollmentIds: enrollmentIds,
                    },
                });
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        evictEnrollmentsFromCache(true);

        return;
    };

    const handleDeleteEnrollments = async (enrollmentIds: string[]) => {
        // Save scroll position before doing the mutation
        prevWindowTop.current = drawerRef?.current?.scrollTop || 0;

        try {
            await deleteEnrollments({
                variables: {
                    offerEventEnrollmentIds: enrollmentIds,
                },
            });
        } catch (error) {
            showApolloError(error);

            return;
        }

        evictEnrollmentsFromCache();

        // Refetch the selector to make sure the correct users are selected
        client.cache.evict({ fieldName: 'paginatedUsers' });
        client.cache.gc();

        return;
    };

    const handleDrawerOpen = (open: boolean) => {
        setDrawerOpen(open);
    };

    const handleExport = async () => {
        try {
            const exportResults = await exportReport({
                variables: {
                    offerEventId: offerEvent.id,
                    q: searchQueryParam,
                    extraFields: getExtraFieldsJSON(selectedFilters || []),
                },
            });
            const fileUrl =
                exportResults.data?.exportOfferEventEnrollment?.fileUrl;

            if (fileUrl) {
                window.open(fileUrl, '_blank');
            } else {
                displaySnackbar(translate('exportDialog.exportSuccess'), {
                    variant: 'success',
                });
            }
        } catch (error) {
            showApolloError(error);

            return;
        }

        setShowExportDialog(false);
    };

    return (
        <>
            <TableAddButton onClick={() => handleDrawerOpen(true)}>
                <Typography fontWeight={700}>
                    {translate('subscriptions')}
                </Typography>
            </TableAddButton>

            <SubscriptionTable
                isEditable
                filterBarAppend={
                    <Button
                        disabled={!enrollments?.length}
                        startIcon={<Icon path={mdiDownload} size="1.9rem" />}
                        sx={{ flex: 1 }}
                        variant="outlined"
                        onClick={() => setShowExportDialog(true)}
                    />
                }
                loading={loading}
                offerEvent={offerEvent}
                paginationProps={{ paginationSettings, setPage }}
                searchProps={{
                    handleSearch: handleSearch,
                    handleFilterSelect: handleFilterSelect,
                    searchQueryParam,
                    filters,
                    filtersLoading,
                    selectedFilters,
                }}
                selectionProps={selectionProps}
                subscriptions={enrollments}
                onDelete={handleDeleteEnrollments}
                onStatusChange={handleUpdateSubscription}
            />

            <UserSelectDrawer
                loading={loading}
                open={drawerOpen}
                selectorQueryVariables={{
                    contentType: IContentTypeValue.OfferEvent,
                    objectId: offerEvent.id,
                }}
                onAddUsers={handleCreateEnrollments}
                onDrawerOpen={handleDrawerOpen}
            />

            <ConfirmDialog
                confirmText={translate('sendExport')}
                loading={loadingExport}
                open={showExportDialog}
                title={translate(
                    `offerEventEnrollmentTable.exportDialog.title`
                )}
                onCancel={() => setShowExportDialog(false)}
                onClose={() => setShowExportDialog(false)}
                onConfirm={handleExport}
            >
                {translate(`offerEventEnrollmentTable.exportDialog.text`, {
                    email: currentUserEmail,
                })}
            </ConfirmDialog>
        </>
    );
};
